import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Appearance, Intent } from '../designConstants';
import { Button } from '../Button';
import { useAppSelector } from '../../redux';
import { isInternalSelector } from '../../redux/user';
import { ErrorReportStatus, sendErrorReport } from '../../utils/error-report';
import { useCopyToClipboard } from '../../utils/hooks/useCopyToClipboard';

interface UseErrorReportArgs {
  referenceId: string;
  errorMessage: string;
  // Identity of the dispatched error. A repeat of the same error is a new object, so it resets the
  // report even though the reference ID is the same.
  error?: object | null;
}

type ReportButtonProps = Pick<ComponentProps<typeof Button>, 'onClick' | 'loading' | 'children'>;

const ReportButton = (props: ReportButtonProps) => (
  <Button appearance={Appearance.OUTLINED} design={Intent.ERROR} {...props} />
);

const SendState = {
  IDLE: 'idle',
  SENDING: 'sending',
  SENT: 'sent',
  COPIED: 'copied',
  FAILED: 'failed'
} as const;
type SendStateType = (typeof SendState)[keyof typeof SendState];

// Report flow for the global error dialog. Returns the body and footer separately. Internal users
// send a report; a failed send copies the ID. Other users only copy the ID.
export const useErrorReport = ({
  referenceId,
  errorMessage,
  error
}: UseErrorReportArgs): { body: ReactNode; footer: ReactNode } => {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const [state, setState] = useState<SendStateType>(SendState.IDLE);
  const { copyToClipboard } = useCopyToClipboard();
  const isInternal = useAppSelector(isInternalSelector);

  // Each dispatched error starts a fresh report, including a repeat of the same error.
  useEffect(() => {
    setNote('');
    setOpen(false);
    setCopyFailed(false);
    setState(SendState.IDLE);
  }, [error]);

  const copyOnly = async (nextState: SendStateType) => {
    const copied = await copyToClipboard(referenceId);
    setCopyFailed(!copied);
    setState(nextState);
  };

  const send = async () => {
    setState(SendState.SENDING);
    try {
      const status = await sendErrorReport({ referenceId, errorMessage, note: note.trim() });
      if (status === ErrorReportStatus.FAILED) throw new Error('Report was not delivered');
      setState(SendState.SENT);
    } catch {
      await copyOnly(SendState.FAILED);
    }
  };

  if (state === SendState.SENT) {
    return {
      body: (
        <p className='error-report-status'>Thank you. Your report has been sent to our team.</p>
      ),
      footer: null
    };
  }

  const copyNote = copyFailed ? (
    <>
      Please send this code to support: <code>{referenceId}</code>
    </>
  ) : (
    'We copied a reference code to your clipboard. Paste it to support so they can find the error.'
  );
  const failedMessage = (state === SendState.FAILED || state === SendState.COPIED) && (
    <p className='error-report-status' role='alert'>
      {state === SendState.FAILED && "We couldn't send your report. "}
      {copyNote}
    </p>
  );

  if (!isInternal) {
    return {
      body: failedMessage || null,
      footer:
        state === SendState.COPIED ? null : (
          <ReportButton onClick={() => copyOnly(SendState.COPIED)}>
            Report this problem
          </ReportButton>
        )
    };
  }

  if (!open) {
    return {
      body: null,
      footer: <ReportButton onClick={() => setOpen(true)}>Report this problem</ReportButton>
    };
  }

  return {
    body: (
      <div className='error-report-form'>
        <Form.Control
          aria-label='Tell us more'
          placeholder='Tell us more (optional)'
          as='textarea'
          rows={3}
          maxLength={1000}
          value={note}
          disabled={state === SendState.SENDING}
          onChange={e => setNote(e.target.value)}
        />
        {failedMessage}
      </div>
    ),
    footer: (
      <ReportButton loading={state === SendState.SENDING} onClick={send}>
        Send
      </ReportButton>
    )
  };
};
